import * as Sentry from '@sentry/react';
import AppConstants from '../Constants/AppConstants';

const SENSITIVE_KEY = /authorization|auth[-_]?key|cookie|password|secret|token|api[-_]?key|private[-_]?key/i;

const redact = (value, key = '') => {
  if (SENSITIVE_KEY.test(String(key))) return '[Filtered]';
  if (Array.isArray(value)) return value.map(item => redact(item));
  if (value && typeof value === 'object') {
    return Object.keys(value).reduce((result, childKey) => {
      result[childKey] = redact(value[childKey], childKey);
      return result;
    }, {});
  }
  return value;
};

const safeError = error => {
  if (error instanceof Error) return error;
  let message;
  try {
    message = typeof error === 'string' ? error : JSON.stringify(error);
  } catch (_) {
    message = String(error);
  }
  return new Error(message || 'Unknown application error');
};

const getRelease = () =>
  process.env.REACT_APP_BUILD_ID ||
  process.env.REACT_APP_VERSION ||
  undefined;

export const initializeSentry = () => {
  const dsn = AppConstants.sentryDNS;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: AppConstants.env || 'production',
    release: getRelease(),
    // Performance tracing is intentionally disabled for now. Exceptions and
    // unhandled promise rejections are still captured by the SDK.
    tracesSampleRate: 0.0,
    attachStacktrace: true,
    maxBreadcrumbs: 100,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb?.data) {
        return {...breadcrumb, data: redact(breadcrumb.data)};
      }
      return breadcrumb;
    },
    beforeSend(event) {
      // Never send cookies, auth headers, or credentials from browser events.
      const sanitized = {...event};
      if (sanitized.request) {
        sanitized.request = {...sanitized.request};
        delete sanitized.request.cookies;
        delete sanitized.request.headers;
        if (sanitized.request.url) {
          sanitized.request.url = String(sanitized.request.url).split('?')[0];
        }
        if (sanitized.request.data) {
          sanitized.request.data = redact(sanitized.request.data);
        }
      }
      if (sanitized.extra) sanitized.extra = redact(sanitized.extra);
      if (sanitized.contexts) sanitized.contexts = redact(sanitized.contexts);
      if (sanitized.breadcrumbs) {
        sanitized.breadcrumbs = sanitized.breadcrumbs.map(breadcrumb =>
          breadcrumb?.data
            ? {...breadcrumb, data: redact(breadcrumb.data)}
            : breadcrumb,
        );
      }
      return sanitized;
    },
  });

  // The build script writes the exact build and commit to this public manifest.
  // Load it after initialization so issues can still be traced to a deployment
  // when the build ID was not supplied as a compile-time environment variable.
  if (typeof window !== 'undefined') {
    fetch(`/build-info.json?ts=${Date.now()}`)
      .then(response => (response.ok ? response.json() : null))
      .then(buildInfo => {
        if (!buildInfo) return;
        Sentry.configureScope(scope => {
          if (buildInfo.build_id) scope.setTag('build_id', String(buildInfo.build_id));
          if (buildInfo.commit_sha) scope.setTag('commit_sha', String(buildInfo.commit_sha));
          scope.setContext('build', redact(buildInfo));
        });
      })
      .catch(() => {});
  }

  return true;
};

export const setSentryContext = () => {
  if (AppConstants.userId) {
    Sentry.setUser({id: String(AppConstants.userId)});
  }
  if (AppConstants.tenantName) {
    Sentry.setTag('tenant_id', String(AppConstants.tenantName));
  }
  if (AppConstants.projectType) {
    Sentry.setTag('project_type', String(AppConstants.projectType));
  }
};

export const captureNetworkFailure = ({
  error,
  path,
  method,
  status,
  service,
  shouldCapture,
}) => {
  const numericStatus = Number(status || error?.status || error?.response?.status || 0);
  const normalizedPath = String(path || '').split('?')[0];
  const context = {
    method: String(method || 'GET').toUpperCase(),
    path: normalizedPath,
    status: numericStatus || undefined,
    service: service || 'api',
  };

  // Authentication, permissions, validation, and not-found responses are
  // expected user-facing outcomes. Keep them as breadcrumbs, while sending
  // transport failures and server failures to the Issues stream.
  Sentry.addBreadcrumb({
    category: 'network',
    message: `${context.method} ${normalizedPath}`,
    level: numericStatus >= 500 || !numericStatus ? 'error' : 'info',
    data: context,
  });

  const capture =
    shouldCapture === undefined
      ? !numericStatus || numericStatus >= 500
      : shouldCapture;
  if (!capture) return;

  Sentry.withScope(scope => {
    scope.setTag('error_source', 'network');
    scope.setContext('request', context);
    Sentry.captureException(safeError(error || `Request failed (${numericStatus || 'network'})`));
  });
};

export default Sentry;
